import { chain } from "lodash"
import { generateClassificationQueryForContributors } from "helpers/classification"
import { buildContributorKey } from "helpers/contributors"
import { getContributorLabel, NOT_HELD } from "connected-components/asset-view/helpers"
import { DEFAULT_HORIZON } from "helpers/forecasts/constants"
import { getGroupByQueryParameters, getGroupByFields, sanitizePositionSet, getGroupBySubqueryParameters } from "../helpers"

const FORECAST_CONTRIBUTOR_FIELDS = `
  assetClass
  country
  id
  modelProviderId
  securityExpectedReturn
  weightedExpectedReturn
  notHeld
`

const FORECAST_GROUP_FIELDS = `
  id
  name
`

function getUngroupedContributorsSubQuery(classificationFields) {
  return `
  forecastContributors (on: $date, forecastId: $forecastId, forecastSecurities: $forecastSecurities, horizon: $horizon, includeNotHeld: $includeNotHeld) {
    date
    contributors{
      ${classificationFields}
      ${FORECAST_CONTRIBUTOR_FIELDS}
    }
  }`
}

function getGroupedContributorsSubQuery(groupByFields, classificationFields) {
  const groupByParams = getGroupBySubqueryParameters(groupByFields)
  return `
  groupedForecastContributors (on: $date, forecastId: $forecastId, forecastSecurities: $forecastSecurities, horizon: $horizon, includeNotHeld: $includeNotHeld ${groupByParams}) {
    date
    groupedContributors {
      ${FORECAST_GROUP_FIELDS}
      contributors {
        ${classificationFields}
        ${FORECAST_CONTRIBUTOR_FIELDS}
      }
    }
  }`
}

function getQuery(groupByFields, classifications) {
  const extraParameters = groupByFields ? getGroupByQueryParameters(groupByFields) : ""
  const classificationFields = generateClassificationQueryForContributors("GICS", classifications)

  const query = `
  query($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $date: Date!, $horizon: Int, $includeNotHeld: Boolean, $forecastId: ShortId, $forecastSecurities: [ForecastSecurityInput!] ${extraParameters}) {
    model(id: $modelId) {
      simulation(positionSet: $positionSet, base: $base, from: $date, to: $date) {
        ${groupByFields
            ? getGroupedContributorsSubQuery(groupByFields, classificationFields)
            : getUngroupedContributorsSubQuery(classificationFields)
        }
      }
    }
  }`
  return query
}

function getForecastReturns(contributor) {
  if (!contributor) {
    return {}
  }

  return {
    weightedExpectedReturn: contributor.weightedExpectedReturn,
    securityExpectedReturn: contributor.securityExpectedReturn,
  }
}

function getForecastTotals(contributors) {
  if (!contributors || contributors.length === 0) {
    return { weightedExpectedReturn: 0, securityExpectedReturn: 0 }
  }

  return contributors.reduce((acc, contributor) => {
    if (contributor.contributors) {
      const nestedTotals = getForecastTotals(contributor.contributors)
      acc.weightedExpectedReturn += nestedTotals.weightedExpectedReturn
    } else {
      acc.weightedExpectedReturn += (contributor.weightedExpectedReturn || 0)
    }
    // The standalone securityExpectedReturn does not aggregate, so we use the weightedExpectedReturn for both totals
    acc.securityExpectedReturn = acc.weightedExpectedReturn
    return acc
  }, { weightedExpectedReturn: 0, securityExpectedReturn: 0 })
}

function parseContributors(prefix, contributors, options) {
  if (!contributors || contributors.length === 0) {
    return {}
  }

  return contributors.reduce((acc, contributor) => {
    if (contributor.notHeld && options.notHeldRollup) {
      if (!acc[NOT_HELD]) {
        acc[NOT_HELD] = {
          label: "Not Held",
          notHeld: true,
          values: {
            [prefix]: { weightedExpectedReturn: 0, securityExpectedReturn: 0 },
          },
        }
      }
      const forecastReturns = getForecastReturns(contributor)
      Object.entries(forecastReturns).forEach(([key, value]) => {
        acc[NOT_HELD].values[prefix][key] = (acc[NOT_HELD].values[prefix][key] ?? 0) + value
      })
    } else {
      acc[buildContributorKey(contributor)] = {
        label: getContributorLabel(contributor),
        notHeld: contributor.notHeld,
        values: {
          [prefix]: getForecastReturns(contributor),
        },
      }
    }
    return acc
  }, {})
}

function parseGroupedContributors(prefix, groups, options) {
  if (!groups || groups.length === 0) {
    return {}
  }

  return chain(groups)
    .keyBy("id")
    .mapValues(group => ({
      label: group.name,
      values: {
        [prefix]: getForecastTotals(group.contributors),
      },
      children: parseContributors(prefix, group.contributors, options),
    }))
    .value()
}

function extractContributors(simulation, groupBy) {
  if (groupBy) {
    return simulation.groupedForecastContributors?.groupedContributors || []
  }
  return simulation.forecastContributors?.contributors || []
}

export function createForecastContributorsQuery({ prefix, modelId, positionSet, base, to, groupByOptions, forecastSecurities, ...options }) {
  const groupByFields = getGroupByFields(groupByOptions)
  const { groupBy, groupById, tier } = groupByFields
  const sanitizedPositionSet = sanitizePositionSet(positionSet)
  const { forecastId, classifications, horizon } = options

  return {
    prefix,
    key: [forecastId, "contributors", prefix, modelId, positionSet, base, to, groupBy, groupById, tier, forecastSecurities],
    query: getQuery(groupBy && groupByFields, classifications),
    variables: {
      modelId,
      positionSet: sanitizedPositionSet,
      date: to,
      base,
      horizon: horizon || DEFAULT_HORIZON,
      ...(forecastSecurities && { forecastSecurities }),
      ...(forecastId && { forecastId }),
      ...groupByFields,
    },
    select: (result) => {
      const simulation = result.data.model.simulation
      if (!simulation?.forecastContributors && !simulation?.groupedForecastContributors) {
        return {
          label: "Total",
          values: {
            [prefix]: { weightedExpectedReturn: 0, securityExpectedReturn: 0 },
          },
        }
      }

      const contributors = extractContributors(simulation, groupBy)
      const children = groupBy
        ? parseGroupedContributors(prefix, contributors, options)
        : parseContributors(prefix, contributors, options)

      return {
        label: "Total",
        values: {
          [prefix]: getForecastTotals(contributors),
        },
        children,
      }
    },
  }
}
